/**
 * Hold a Best Available pick, and when another buyer got there first, move
 * to the next best seats and try again — instead of a dead end.
 *
 * Best Available is a read; the hold is the arbiter. At a drop moment every
 * buyer who searches in the same second is sent to the same "best" pair, so
 * all but one get "Seat is held" and, before this, a dead end (20 of 100
 * buyers in the 2026-09-08 load test). A real buyer's next move is to search
 * again; this does that for them, up to `maxAttempts` times.
 *
 * Only for an UNTOUCHED Best Available pick. A buyer who chose seats by hand
 * chose those seats — for them a collision is an error to show, not a
 * reason to hand them different ones. The caller decides which case it is
 * by passing `research: null`.
 *
 * Pure orchestration: every side effect (the hold request, the search, the
 * selection swap) is injected, so this is unit-tested without a browser.
 */

export interface HoldOutcome {
  ok: boolean
  /** Server's reason code, e.g. SINGLE_SEAT_BLOCKED — never retried. */
  code?: string
  error?: string
  failedSeatIds?: string[]
  data?: unknown
}

export interface HoldWithRetryDeps<Seat> {
  hold: (seatIds: string[]) => Promise<HoldOutcome>
  /** Re-run the buyer's search; the next best seats, or null/[] when there are none. `null` here means "hand-picked — do not retry". */
  research: (() => Promise<Seat[] | null>) | null
  /** Swap the selection to these seats; returns the seat ids to hold next. */
  reselect: (seats: Seat[]) => Promise<string[]>
  /** Total hold attempts, including the first. Default 3. */
  maxAttempts?: number
}

export interface HoldWithRetryResult {
  outcome: HoldOutcome
  /** The seat ids the final attempt was made with — the ones held, on success. */
  seatIds: string[]
  attempts: number
  /** Attempts that failed because the seats were taken; 0 means the first pick held. */
  collisions: number
}

export async function holdWithRetry<Seat>(
  seatIds: string[],
  deps: HoldWithRetryDeps<Seat>
): Promise<HoldWithRetryResult> {
  const maxAttempts = Math.max(1, deps.maxAttempts ?? 3)
  let ids = seatIds
  let collisions = 0
  let outcome: HoldOutcome = { ok: false, error: 'No seats to hold' }
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    outcome = await deps.hold(ids)
    if (outcome.ok) return { outcome, seatIds: ids, attempts: attempt, collisions }
    // A rule refusal (single seat left stranded) is about the SHAPE of the
    // pick, not who got there first — searching again will not change it.
    if (outcome.code === 'SINGLE_SEAT_BLOCKED') break
    if (!deps.research) break
    collisions++
    if (attempt === maxAttempts) break
    const next = await deps.research()
    if (!next || next.length === 0) break
    ids = await deps.reselect(next)
    if (ids.length === 0) break
  }
  return { outcome, seatIds: ids, attempts: Math.min(collisions + (outcome.ok ? 1 : 0), maxAttempts) || 1, collisions }
}
